import { useQuery } from '@apollo/client';
import React, { useEffect } from 'react';
import { gql } from '../../graphql/generated/gql';
import { TopicPrivacy } from '../../graphql/generated/graphql';
import topicIcon from '../../icons/prefilled/16x16/topic.svg';
import TopicPrivacyIcon from '../../topic/TopicPrivacyIcon';
import topicPath, { publicTopicPath } from '../../utils/url/topicPath';
import useIsPublicView from '../../utils/useIsPublicView';
import MentionBase from './MentionBase';
import { useAllTopicMap } from '../../topic/useAllTopics';

const PUBLIC_TOPIC_QUERY = gql(/** @complexity 1 */ `
  query TopicMentionPublic($id: ID!) {
    topic: publicTopic(id: $id) {
      id
      name
      privacy
    }
  }
`);

interface TopicMentionProps {
  id: string;
  content: string | null;
  mentionText?: string | null;
  updateCache?: (name?: string) => void;
  link: boolean;
  singleLine?: boolean;
  newTab?: boolean;
}

const TopicMention = ({
  id,
  content,
  mentionText,
  updateCache,
  link,
  singleLine,
  newTab,
}: TopicMentionProps) => {
  const isPublicView = useIsPublicView();

  const topicsMap = useAllTopicMap();
  const { data: publicData } = useQuery(PUBLIC_TOPIC_QUERY, {
    variables: { id },
    skip: !isPublicView,
  });

  const topic = topicsMap[id] ?? publicData?.topic;
  const isPublic = topic?.privacy === TopicPrivacy.Public;

  const path =
    isPublic && isPublicView
      ? publicTopicPath(topic ?? { id, name: content ?? '' })
      : topicPath(topic ?? { id, name: content ?? '' });

  useEffect(() => {
    if (!isPublicView && topic?.name != null && content == null) {
      updateCache?.(topic.name);
    }
  }, [updateCache, isPublicView, topic?.name, content]);

  return (
    <MentionBase
      icon={topicIcon}
      type="topic"
      to={link ? path : undefined}
      content={mentionText ?? topic?.name ?? content ?? undefined}
      newTab={newTab}
      singleLine={singleLine}
      iconAfter={topic && <TopicPrivacyIcon topic={topic} iconSize="medium" />}
    />
  );
};

export default TopicMention;
